import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type { ReactNode } from "react";

import { API_BASE } from "../api/base";

interface AuthUser {
  id: string;
  name: string;
  email: string;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  clearAuth: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// localStorage rather than a cookie, deliberately: the Cloudflare Access JWT
// this token is exchanged from is itself script-readable on this origin
// (CF_Authorization is not HttpOnly here - verified live on staging), so a
// stored app token adds no exposure class an XSS would not already have.
// Feed-sourced content is rendered as escaped text with scheme-checked links.
const STORAGE_KEY = "lodge_auth";

interface StoredAuth {
  token: string;
  user: AuthUser;
  expiresAt: number;
}

function getStoredAuth(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored: StoredAuth = JSON.parse(raw);
    if (Date.now() > stored.expiresAt) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return stored;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

function storeAuth(token: string, user: AuthUser) {
  // A token we cannot read an expiry from must be refused, not stored: an
  // unparseable payload would throw a bare InvalidCharacterError later, and
  // a missing exp would make the entry immortal (Date.now() > NaN is false),
  // leaving the 401 path as the only way to ever notice staleness.
  let expiresAt: number;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    expiresAt = payload.exp * 1000;
  } catch {
    throw new Error("The exchange returned a token that could not be parsed");
  }
  if (!Number.isFinite(expiresAt)) {
    throw new Error("The exchange returned a token without an expiry");
  }

  const stored: StoredAuth = {
    token,
    user,
    expiresAt,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp("(^| )" + name + "=([^;]+)"),
  );
  return match ? match[2] : null;
}

interface ExchangeResponse {
  token: string;
  user: AuthUser;
}

async function exchange(
  body: { token: string } | { email: string },
): Promise<ExchangeResponse> {
  const response = await fetch(`${API_BASE}/auth/exchange`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(
      errorBody.message || `Exchange failed: ${response.status}`,
    );
  }
  return response.json();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const stored = getStoredAuth();
    if (stored) {
      return {
        token: stored.token,
        user: stored.user,
        isLoading: false,
        isAuthenticated: true,
        error: null,
      };
    }
    return {
      token: null,
      user: null,
      isLoading: true,
      isAuthenticated: false,
      error: null,
    };
  });

  const clearAuth = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setState({
      token: null,
      user: null,
      isLoading: false,
      isAuthenticated: false,
      error: null,
    });
  }, []);

  useEffect(() => {
    if (state.isAuthenticated) return;

    let cancelled = false;

    async function authenticate() {
      try {
        let result: ExchangeResponse;

        if (import.meta.env.DEV) {
          // Overridable so local dev works for any account that the
          // backend's allowed-domain check accepts.
          result = await exchange({
            email: import.meta.env.VITE_LODGE_DEV_EMAIL || "martin@folarity.org",
          });
        } else {
          const cfToken = getCookie("CF_Authorization");
          if (!cfToken) {
            if (!cancelled) {
              setState((prev) => ({
                ...prev,
                isLoading: false,
                error: "No Cloudflare Access token found",
              }));
            }
            return;
          }
          result = await exchange({ token: cfToken });
        }

        if (!cancelled) {
          storeAuth(result.token, result.user);
          setState({
            token: result.token,
            user: result.user,
            isLoading: false,
            isAuthenticated: true,
            error: null,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error:
              err instanceof Error
                ? err.message
                : "Authentication failed",
          }));
        }
      }
    }

    authenticate();
    return () => {
      cancelled = true;
    };
  }, [state.isAuthenticated]);

  const value = useMemo(
    () => ({ ...state, clearAuth }),
    [state, clearAuth],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function getStoredToken(): string | null {
  const stored = getStoredAuth();
  return stored?.token ?? null;
}

export function clearStoredToken() {
  localStorage.removeItem(STORAGE_KEY);
}
